import React, { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from '@/components/ui/button';
import { Check, X, RotateCw } from 'lucide-react';
import { CropAreaData } from './CropArea';

interface CropInterfaceProps {
  image: string;
  pageIndex: number;
  onCropComplete: (crop: CropAreaData) => void;
  onCancel: () => void;
}

export const CropInterface = ({ image, pageIndex, onCropComplete, onCancel }: CropInterfaceProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const imageRef = useRef<HTMLImageElement>();

  const onCropChangeHandler = useCallback((crop: { x: number; y: number }) => {
    setCrop(crop);
  }, []);

  const onCropAreaChangeHandler = useCallback((croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createCroppedImage = useCallback(async () => {
    if (!croppedAreaPixels) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx || !imageRef.current) return;

    // Set canvas size to the cropped area
    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;

    // Create a new image for cropping
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    return new Promise<Blob>((resolve, reject) => {
      img.onload = () => {
        // Draw the cropped portion
        ctx.drawImage(
          img,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          croppedAreaPixels.width,
          croppedAreaPixels.height
        );

        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create crop'));
          }
        }, 'image/png', 0.95);
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = image;
    });
  }, [croppedAreaPixels, image]);

  const handleSaveCrop = async () => {
    try {
      const blob = await createCroppedImage();
      if (!blob || !croppedAreaPixels) return;

      const cropData: CropAreaData = {
        id: Date.now().toString(),
        blob,
        pageIndex,
        cropArea: croppedAreaPixels,
        previewUrl: URL.createObjectURL(blob)
      };

      onCropComplete(cropData);
    } catch (error) {
      console.error('Error creating crop:', error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex flex-col">
      <div className="flex-1 relative">
        <Cropper
          image={image}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={undefined}
          onCropChange={onCropChangeHandler}
          onZoomChange={setZoom}
          onCropAreaChange={onCropAreaChangeHandler}
          showGrid={true}
          style={{
            containerStyle: {
              width: '100%',
              height: '100%',
              backgroundColor: 'black'
            },
            cropAreaStyle: {
              border: '2px solid hsl(var(--primary))',
              color: 'hsl(var(--primary))'
            }
          }}
        />
      </div>
      
      <div className="bg-background/90 backdrop-blur-sm border-t p-4">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRotation(prev => prev + 90)}
            >
              <RotateCw className="w-4 h-4 mr-2" />
              Rotate
            </Button>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Zoom:</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-24"
              />
              <span className="text-sm text-muted-foreground">{Math.round(zoom * 100)}%</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onCancel}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSaveCrop} disabled={!croppedAreaPixels}>
              <Check className="w-4 h-4 mr-2" />
              Save Crop
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};